import { Toast } from '@figma/fpl-components';
import { Icon24Warning } from '@figma/fpl-icons';
import * as stylex from '@stylexjs/stylex';
import { Alert, Snackbar, type SxProps } from '@mui/material';
import { forwardRef, type ForwardedRef, type ReactNode } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { FF_FPL_MIGRATION } from '@/consts/featureFlags';
import { ZIndex } from '@/consts/z-indexes.consts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Typography } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { WarningCircleIcon } from '../Icons';

const styles = stylex.create({
  fplHost: {
    position: 'fixed',
    top: 15,
    right: 8,
    left: 8,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  fplEntry: {
    pointerEvents: 'auto',
  },
  zIndex: (value: number) => ({ zIndex: value }),
});

export interface ErrorSnackbarActionProps {
  label: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
}

export interface ErrorSnackbarProps {
  open: boolean;
  errorMessage: ReactNode;
  action?: ErrorSnackbarActionProps;
}

type ErrorAlertProps = Pick<ErrorSnackbarProps, 'action'> & {
  errorMessage: ReactNode;
  sx?: SxProps;
};

export const ErrorAlert = forwardRef<HTMLDivElement, ErrorAlertProps>(function ErrorAlert(
  { action, errorMessage, sx = {} }: ErrorAlertProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <Alert
      ref={ref}
      severity="error"
      icon={<WarningCircleIcon style={{ height: 20, width: 20 }} color={token.textDanger} />}
      sx={{
        backgroundColor: color.Weavy_Error_08,
        border: `1px solid ${color.Weavy_Error_64_T}`,
        borderRadius: '8px',
        color: token.textDanger,
        display: 'flex',
        alignItems: 'center',
        ...sx,
      }}
      slots={{ message: Message }}
      slotProps={{ action: { sx: { padding: '0 0 0 16px' } } }}
      action={
        action ? (
          <ButtonContained
            mode="filled-light-secondary"
            size="xs"
            onClick={() => void action.onClick()}
            disabled={action.disabled}
            loading={action.loading}
          >
            {action.label}
          </ButtonContained>
        ) : undefined
      }
    >
      {errorMessage}
    </Alert>
  );
});

function ErrorSnackbar({ open, action, errorMessage }: ErrorSnackbarProps) {
  const isFpl = useFeatureFlag(FF_FPL_MIGRATION).value === true;

  if (isFpl) {
    if (!open) return null;
    return (
      <div
        {...stylex.props(styles.fplHost, styles.zIndex(ZIndex.Overlays.APP_SNACKBAR))}
        data-testid="fpl-error-snackbar-host"
      >
        <div {...stylex.props(styles.fplEntry)}>
          <Toast.Root data-testid="fpl-error-snackbar" variant="danger">
            <Icon24Warning aria-hidden="true" />
            <Toast.Message role="alert">{errorMessage}</Toast.Message>
            {action && (
              <Toast.ActionButton
                action={() => {
                  void action.onClick();
                  return false;
                }}
                disabled={action.disabled || action.loading}
                loading={action.loading}
              >
                {action.label}
              </Toast.ActionButton>
            )}
          </Toast.Root>
        </div>
      </div>
    );
  }

  return (
    <Snackbar open={open} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <ErrorAlert action={action} errorMessage={errorMessage} />
    </Snackbar>
  );
}

const Message = ({ children }: { children: ReactNode }) => (
  <Typography variant="body-sm-md" color={token.textDanger} sx={{ whiteSpace: 'pre-line' }}>
    {children}
  </Typography>
);

export default ErrorSnackbar;
